import { Link } from "@tanstack/react-router";
import type { ConnectionSummary } from "@/api/queries";
import { AlertIcon, ArrowRightIcon } from "@/components/icons";

/**
 * The gap an engine without comments leaves (PRD FR-2.1).
 *
 * SQLite stores no table or column descriptions at all, so a source on it
 * tells the agent nothing about what its columns mean. That is a known and
 * accepted limitation rather than a bug — but it is only survivable if
 * somebody writes the Data Dictionary, so a source with neither says so here
 * instead of quietly producing worse answers later.
 */
export function UndocumentedNotice({ connection }: { connection: ConnectionSummary }) {
  if (connection.has_native_comments || connection.has_data_dictionary) return null;

  return (
    <div className="flex items-start gap-3 rounded-container border border-warning bg-warning-subtle p-4">
      <span className="text-warning mt-0.5">
        <AlertIcon className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="type-label text-ink">This source has no documentation</p>
        <p className="type-support text-ink-secondary mt-1">
          {connection.source_type === "sqlite" ? "SQLite" : "This engine"} stores no table or
          column descriptions, and no Data Dictionary has been written — so nothing tells the
          agent what these columns mean, which metric is which, or how to join them. Answers will
          be guesses until someone writes it.
        </p>
        <Link
          to="/sources/$connectionId"
          params={{ connectionId: connection.id }}
          search={{ tab: "knowledge" }}
          className="type-support text-accent mt-2 inline-flex items-center gap-1 hover:underline"
        >
          Write the Data Dictionary
          <ArrowRightIcon className="size-3.5" />
        </Link>
      </div>
    </div>
  );
}
